import { Component, OnInit, OnDestroy, inject, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { QuotaService } from '../../services/quota.service';
import { QuotaCardComponent } from '../../components/quota-card/quota-card.component';
import { QuotaHeaderComponent } from '../../components/quota-header/quota-header.component';
import { Quota } from '../../models/quota.interface';
import { AuthService } from '../../../../core/services/auth.service';
import { Subscription, interval } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-home-page',
  standalone: true,
  imports: [CommonModule, QuotaCardComponent, QuotaHeaderComponent, MatProgressSpinnerModule, MatIconModule, MatButtonModule, TranslateModule],
  templateUrl: './home-page.component.html',
  styleUrls: ['./home-page.component.css']
})
export class HomePageComponent implements OnInit, OnDestroy {
  private quotaService = inject(QuotaService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private pollingSubscription?: Subscription;

  quotas: Quota[] = [];
  loading = true;
  error: string | null = null;

  get isAuthenticated(): boolean {
    return this.authService.isAuthenticated();
  }

  get isSupplier(): boolean {
    return this.authService.hasRole('quota_supplier');
  }


  ngOnInit(): void {
    this.loadQuotas();
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  private startPolling(): void {
    this.pollingSubscription = interval(5000).pipe(
      switchMap(() => {
        if (this.authService.isAuthenticated()) {
          return this.quotaService.getAllQuotas({
            page: 1,
            size: 15,
            status: 'ACTIVE'
          });
        } else {
          return this.quotaService.getPublicQuotas();
        }
      })
    ).subscribe({
      next: (response) => {
        this.quotas = response.res.content;
      },
      error: (err) => {
        console.error('Polling error:', err);
      }
    });
  }

  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  loadQuotas(): void {
    this.loading = true;
    this.error = null;

    if (this.authService.isAuthenticated()) {
      // Authenticated user - load all quotas with filters
      this.quotaService.getAllQuotas({
        page: 1,
        size: 15,
        status: 'ACTIVE'
      }).subscribe({
        next: (response) => {
          this.quotas = response.res.content;
          this.loading = false;
        },
        error: (err) => {
          this.error = this.extractErrorMessage(err);
          this.loading = false;
          console.error('Ошибка загрузки квот (authenticated):', err);
        }
      });
    } else {
      // Not authenticated - load public quotas
      this.quotaService.getPublicQuotas().subscribe({
        next: (response) => {
          this.quotas = response.res.content;
          this.loading = false;
        },
        error: (err) => {
          this.error = this.extractErrorMessage(err);
          this.loading = false;
          console.error('Ошибка загрузки квот (public):', err);
        }
      });
    }
  }

  private extractErrorMessage(err: any): string {
    const body = err?.error;
    if (body?.res?.ru) {
      return body.res.ru;
    }
    if (body?.msg) {
      return body.msg;
    }
    return 'Не удалось загрузить данные';
  }

  retry(): void {
    this.loadQuotas();
  }
}
